import { CalendarClock, CircleCheck, CircleStop, Pause, Zap, type LucideIcon } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import type { DosingSchedule } from '../../services/dosingScheduleService';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { OverflowMenu } from '../atoms/OverflowMenu';
import { defaultDoseTimes } from './doseTimes';
import { formatDoseDateTime, formatPlanDate } from './formatDoseDateTime';
import { formatNumber } from '../measurements/measurementHistoryUtils';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import type { DosingPlanStatus } from '../../services/dosingScheduleService';
import './DosingScheduleCard.css';

const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export interface DosingScheduleCardProps {
  schedule: DosingSchedule;
  onEdit: (schedule: DosingSchedule) => void;
  onDelete: (schedule: DosingSchedule) => void;
  /** Logs one dose of this plan now. Without it the action is shown disabled. */
  onLogNow?: (schedule: DosingSchedule) => void;
  /** Opens the pause dialog ("Pausieren"). Without it the action is not shown. */
  onPause?: (schedule: DosingSchedule) => void;
  /** Resumes a paused plan ("Fortsetzen"). */
  onResume?: (schedule: DosingSchedule) => void;
}

const STATUS_BADGE: Record<DosingPlanStatus, { tone: BadgeTone; icon: LucideIcon }> = {
  active: { tone: 'success', icon: CircleCheck },
  paused: { tone: 'warning', icon: Pause },
  scheduled: { tone: 'info', icon: CalendarClock },
  ended: { tone: 'neutral', icon: CircleStop },
};

/** "2× täglich", "Alle 3 Tage", … — the frequency together with its count. */
function frequencySummaryKey(schedule: DosingSchedule): { key: string; n: number | null } {
  switch (schedule.frequency) {
    case 'TIMES_PER_DAY':
      return { key: 'dosing.card.frequencySummary.TIMES_PER_DAY', n: schedule.times_per_day ?? 1 };
    case 'TIMES_PER_WEEK':
      return { key: 'dosing.card.frequencySummary.TIMES_PER_WEEK', n: schedule.times_per_day ?? 1 };
    // An interval of 1 reads "1× täglich" / "Wöchentlich", never "Alle 1 Tage" (audit §3.6).
    case 'EVERY_X_DAYS':
      return (schedule.interval_days ?? 1) === 1
        ? { key: 'dosing.card.frequencySummary.TIMES_PER_DAY', n: 1 }
        : { key: 'dosing.card.frequencySummary.EVERY_X_DAYS', n: schedule.interval_days ?? 1 };
    case 'EVERY_X_WEEKS':
      return (schedule.interval_days ?? 1) === 1
        ? { key: 'dosing.form.frequencyOptions.WEEKLY', n: null }
        : { key: 'dosing.card.frequencySummary.EVERY_X_WEEKS', n: schedule.interval_days ?? 1 };
    default:
      return { key: `dosing.form.frequencyOptions.${schedule.frequency}`, n: null };
  }
}

export function DosingScheduleCard({ schedule, onEdit, onDelete, onLogNow, onPause, onResume }: DosingScheduleCardProps) {
  const { t, i18n } = useTranslation();
  const isPump = schedule.dosing_pump === true;
  const language = i18n?.language;
  const status: DosingPlanStatus = schedule.status ?? 'active';
  const StatusIcon = STATUS_BADGE[status].icon;
  const statusLabel =
    status === 'paused'
      ? schedule.paused_until
        ? t('dosing.card.status.pausedUntil', { date: formatPlanDate(schedule.paused_until, language) })
        : t('dosing.card.status.paused')
      : status === 'scheduled' && schedule.start_date
        ? t('dosing.card.status.scheduled', { date: formatPlanDate(schedule.start_date, language) })
        : t(`dosing.card.status.${status === 'scheduled' ? 'active' : status}`);
  const period =
    schedule.start_date || schedule.end_date
      ? schedule.end_date
        ? t('dosing.card.periodRange', {
            from: schedule.start_date ? formatPlanDate(schedule.start_date, language) : '…',
            to: formatPlanDate(schedule.end_date, language),
          })
        : t('dosing.card.periodFrom', { from: formatPlanDate(schedule.start_date ?? '', language) })
      : null;
  const inactiveHint =
    status === 'paused' ? t('dosing.card.pausedHint') : status === 'ended' ? t('dosing.card.endedHint') : null;
  const hintId = `dosing-schedule-hint-${schedule.id}`;

  const summary = frequencySummaryKey(schedule);
  const frequencyLabel = summary.n !== null ? t(summary.key, { n: summary.n }) : t(summary.key);

  const weekdayLabels =
    schedule.weekdays && schedule.weekdays.length > 0
      ? schedule.weekdays
          .slice()
          .sort((a, b) => a - b)
          .map((day) => {
            const key = WEEKDAY_KEYS[day];
            return key ? t(`dosing.form.weekdayLabels.${key}`) : '';
          })
          .filter((label) => label.length > 0)
          .join(', ')
      : null;

  // "N× per day" plans dose at their stored times or the defaults (pump: logged
  // automatically; manual: due then in Aufgaben).
  const pumpDoseTimes =
    schedule.frequency === 'TIMES_PER_DAY'
      ? schedule.dose_times && schedule.dose_times.length === (schedule.times_per_day ?? 1)
        ? schedule.dose_times
        : defaultDoseTimes(schedule.times_per_day ?? 1)
      : null;

  const nextAutoLog = isPump && schedule.next_auto_log_at
    ? formatDoseDateTime(schedule.next_auto_log_at, i18n?.language)
    : null;

  const logNowLabel = isPump ? t('dosing.card.logExtraDose') : t('dosing.card.logNow');

  return (
    <div className="dosing-schedule-card">
      <div className="dosing-schedule-card__body">
        <div className="dosing-schedule-card__title">
          <Heading level={3}>{schedule.product_name}</Heading>
          <Badge
            tone={STATUS_BADGE[status].tone}
            className="dosing-schedule-card__pump-badge"
            data-testid="dosing-schedule-status"
            data-status={status}
          >
            <StatusIcon size={14} aria-hidden="true" focusable="false" />
            {statusLabel}
          </Badge>
          {isPump && (
            <Badge tone="info" className="dosing-schedule-card__pump-badge" data-testid="dosing-schedule-pump-badge">
              <Zap size={14} aria-hidden="true" focusable="false" />
              {t('dosing.card.pumpBadge')}
            </Badge>
          )}
        </div>

        <Text>
          <strong>{t('dosing.card.doseLabel')}:</strong> {formatNumber(schedule.dose_amount, language)}{' '}
          {schedule.dose_unit}
        </Text>

        <Text data-testid="dosing-schedule-frequency">
          <strong>{t('dosing.card.frequencyLabel')}:</strong> {frequencyLabel}
        </Text>

        {weekdayLabels && (
          <Text data-testid="dosing-schedule-weekdays">
            <strong>{t('dosing.form.weekdays')}:</strong> {weekdayLabels}
          </Text>
        )}

        {pumpDoseTimes ? (
          <Text data-testid="dosing-schedule-dose-times">
            <strong>{t('dosing.card.timeLabel')}:</strong> {pumpDoseTimes.join(', ')}
          </Text>
        ) : (
          schedule.time_of_day && (
            <Text>
              <strong>{t('dosing.card.timeLabel')}:</strong> {schedule.time_of_day}
            </Text>
          )
        )}

        {period && (
          <Text data-testid="dosing-schedule-period">
            <strong>{t('dosing.card.periodLabel')}:</strong> {period}
          </Text>
        )}

        {nextAutoLog && status === 'active' && (
          <Text data-testid="dosing-schedule-next-auto-log">
            <strong>{t('dosing.card.nextAutoLog')}:</strong> {nextAutoLog}
          </Text>
        )}

        {schedule.notes && (
          <Text>
            <strong>{t('dosing.card.notesLabel')}:</strong> {schedule.notes}
          </Text>
        )}

        {(isPump || inactiveHint) && (
          <Text variant="muted" size="sm" id={hintId} data-testid="dosing-schedule-hint">
            {[inactiveHint, isPump ? t('dosing.card.pumpHint') : null].filter(Boolean).join(' ')}
          </Text>
        )}

        {!isPump && status !== 'ended' && (
          <Text variant="muted" size="sm" data-testid="dosing-schedule-tasks-hint">
            {t('dosing.card.tasksHint')}
          </Text>
        )}

        <div className="dosing-schedule-card__actions">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onEdit(schedule)}
            aria-label={t('dosing.card.edit')}
          >
            {t('dosing.card.edit')}
          </Button>

          {status === 'paused' && onResume && (
            <Button type="button" variant="secondary" size="sm" onClick={() => onResume(schedule)} data-testid="dosing-schedule-resume">
              {t('dosing.card.resume')}
            </Button>
          )}
          {(status === 'active' || status === 'scheduled') && onPause && (
            <Button type="button" variant="secondary" size="sm" onClick={() => onPause(schedule)} data-testid="dosing-schedule-pause">
              {t('dosing.card.pause')}
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-disabled={onLogNow ? undefined : 'true'}
            onClick={onLogNow ? () => onLogNow(schedule) : undefined}
            aria-describedby={isPump || inactiveHint ? hintId : undefined}
            data-testid="dosing-schedule-log-now"
          >
            {logNowLabel}
          </Button>

          {/* Delete is not a routine action: in the "···" menu, not between Edit and Pause (usability audit §5.8). */}
          <OverflowMenu
            className="dosing-schedule-card__more"
            label={t('common.moreActionsFor', { name: schedule.product_name })}
            items={[{ id: 'delete', label: t('dosing.card.delete'), onSelect: () => onDelete(schedule), tone: 'danger' }]}
          />
        </div>
      </div>
    </div>
  );
}
